import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
  Checkbox,
  Autocomplete,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { fetchPaginatedData } from "../../../helper/common/fetchPaginatedData";
import { masterApi } from "../../../apis/masterApi";
import { toast } from "react-toastify";
import axios from "axios";
import { useGlobalContext } from "../../../context/global";
import { campaignMappingModalStyle as style } from "../productSetsStyles";

export default function CampaignMappingModal({
  open,
  onClose,
  userPlatform,
  productsetId,
  entity_populate_list,
  onRefetch = () => {},
}) {
  const navigate = useNavigate();
  const { clientId } = useGlobalContext();
  // Flipkart: campaigns can be linked without ad-groups. Other platforms still require ad-groups.
  const allowCampaignOnly = userPlatform === "flipkart";
  const [isSaving, setIsSaving] = useState(false);
  const [isCampaignsFetching, setIsCampaignsFetching] = useState(false);
  const [searchFiltersState, setSearchFiltersState] = useState({
    campaigns: [],
    adGroups: [],
  });

  // store selected IDs
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedAdGroups, setSelectedAdGroups] = useState([]);
  const [errors, setErrors] = useState({
    campaigns: "",
    adGroups: "",
  });

  const getAllCampaigns = async () => {
    const requestConfig = (fields) => ({
      source: userPlatform,
      fields,
      limit: 5000,
      filtering: [],
    });

    try {
      setIsCampaignsFetching(true);

      const [campaignsResponse, adgroupResponse] = await Promise.all([
        fetchPaginatedData(
          masterApi,
          requestConfig("id,name,platform_id,status"),
          5000,
          3,
          "campaignList",
          false
        ),
        fetchPaginatedData(
          masterApi,
          requestConfig("id,name,platform_id,campaign_id"),
          5000,
          12,
          "adsetList",
          false
        ),
      ]);

      const adgroups = (adgroupResponse ?? [])
        .filter((a) => a?.platform_id != null)
        .map((a) => ({
          label: a.name,
          value: a.id,
          campaign_id: a.campaign_id,
        }));

      const campaigns = (campaignsResponse ?? [])
        .filter((c) => c?.platform_id != null)
        .map((c) => {
          const hasAdGroup = adgroups.some((a) => a.campaign_id === c.id);
          return {
            label: c.name,
            value: c.id,
            status: c.status,
            hasAdGroup, // new key
          };
        });

      setSearchFiltersState({ campaigns, adGroups: adgroups });
    } catch (error) {
      console.error(error);
      toast.error("Error fetching campaigns/adgroups");
    } finally {
      setIsCampaignsFetching(false);
    }
  };

  function mergeCampaignsAndAdgroups(campaignData, adgroupData) {
    return (adgroupData ?? []).reduce((acc, curr) => {
      const foundCampaign = (campaignData ?? []).find(
        (c) => c.value === curr.campaign_id
      );
      if (foundCampaign) {
        acc.push({
          label: `${curr.label} (Camp: ${foundCampaign.label})`,
          value: curr.value, // adgroup id
          campaign_id: foundCampaign.value,
          campaign_label: foundCampaign.label,
        });
      }
      return acc;
    }, []);
  }

  const mergedData = useMemo(() => {
    return mergeCampaignsAndAdgroups(
      searchFiltersState?.campaigns ?? [],
      searchFiltersState?.adGroups ?? []
    );
  }, [searchFiltersState]);

  const mergedDataOptionsForDropDown = useMemo(() => {
    if (!mergedData) return [];
    if (!selectedCampaigns?.length) return mergedData;
    return mergedData.filter((m) => selectedCampaigns.includes(m.campaign_id));
  }, [mergedData, selectedCampaigns]);

  function formatDataForProductSet() {
    // When ad-groups are selected, map from those (includes campaign_id + adset_id)
    if (selectedAdGroups?.length) {
      const selectedCampaignsAndAdgroups = selectedAdGroups.map((adgroupId) => {
        const found = mergedData?.find((data) => data?.value === adgroupId);
        return {
          campaign_id: found?.campaign_id,
          adset_id: found?.value,
        };
      });

      // Flipkart: also include selected campaigns that have no ad-group picked
      if (allowCampaignOnly && selectedCampaigns?.length) {
        const campaignsCoveredByAdGroups = new Set(
          selectedCampaignsAndAdgroups.map((e) => e.campaign_id)
        );
        selectedCampaigns.forEach((campaignId) => {
          if (!campaignsCoveredByAdGroups.has(campaignId)) {
            selectedCampaignsAndAdgroups.push({
              campaign_id: campaignId,
              adset_id: null,
            });
          }
        });
      }

      return {
        source: userPlatform,
        entity_populate_list: selectedCampaignsAndAdgroups,
      };
    }

    // Flipkart campaign-only: no ad-groups selected
    if (allowCampaignOnly && selectedCampaigns?.length) {
      return {
        source: userPlatform,
        entity_populate_list: selectedCampaigns.map((campaignId) => ({
          campaign_id: campaignId,
          adset_id: null,
        })),
      };
    }

    return {
      source: userPlatform,
      entity_populate_list: [],
    };
  }

  async function handleSaveProductSet() {
    if (!selectedCampaigns?.length && selectedAdGroups?.length) {
      setErrors((prev) => ({
        ...prev,
        campaigns: "Please select at least one campaign.",
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, campaigns: "" }));
    }

    // Non-Flipkart platforms still require at least one ad group when campaigns are selected
    if (
      !allowCampaignOnly &&
      !selectedAdGroups?.length &&
      selectedCampaigns?.length
    ) {
      setErrors((prev) => ({
        ...prev,
        adGroups: "Please select at least one ad group.",
      }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, adGroups: "" }));
    }
    const formattedData = formatDataForProductSet();

    try {
      setIsSaving(true);
      const url = `${process.env.REACT_APP_ADS_API}/client/${clientId}/product-set/${productsetId}?source=${userPlatform}`;
      const data = productsetId
        ? await axios.patch(url, formattedData, {
            headers: { "Content-Type": "application/merge-patch+json" },
          })
        : await axios.post(url, formattedData);

      if (data.data?.success && productsetId) {
        toast.success("Product set populated successfully!");
        navigate("/product-set-list");
        onClose();
        onRefetch();
      } else {
        toast.error("Error while saving product set. Try Again!");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Some unknown error occurred!";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    getAllCampaigns();
  }, []);

  useEffect(() => {
    if (entity_populate_list?.length) {
      const campaignIds = [
        ...new Set(
          entity_populate_list.map((e) => e.campaign_id).filter(Boolean)
        ),
      ];
      // Filter out null/undefined adset_ids (Flipkart campaign-only mappings)
      const adgroupIds = entity_populate_list
        .map((e) => e.adset_id)
        .filter(Boolean);
      setSelectedCampaigns(campaignIds);
      setSelectedAdGroups(adgroupIds);
    }
  }, [entity_populate_list]);

  return (
    <Modal
      open={!!open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          px={2.5}
          pt={2.5}
        >
          <Typography variant="subtitle1">Link to Campaign</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ background: "#E0E0E0" }} />

        <Typography variant="subtitle1" mt={2.5} px={2.5}>
          Campaign Details
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" px={2.5}>
          Select campaigns and ad groups to populate the product set
        </Typography>

        {isCampaignsFetching ? (
          <Box
            p={2.5}
            minHeight={200}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3} p={2.5} mb={4}>
            {/* Campaign Select */}
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={searchFiltersState.campaigns ?? []}
              getOptionLabel={(opt) => opt.label}
              value={searchFiltersState.campaigns.filter((c) =>
                selectedCampaigns.includes(c.value)
              )}
              onChange={(e, newValue, reason, details) => {
                setErrors((prev) => ({ ...prev, campaigns: "" }));
                // Non-Flipkart: prevent selection if campaign has no ad-group
                if (
                  !allowCampaignOnly &&
                  details?.option &&
                  !details.option.hasAdGroup
                ) {
                  return;
                }
                const ids = newValue.map((v) => v.value);
                setSelectedCampaigns(ids);
                setSelectedAdGroups([]); // reset adgroups
              }}
              getOptionDisabled={(option) =>
                !allowCampaignOnly && !option.hasAdGroup
              }
              PopperProps={{
                sx: { "& .MuiPaper-root": { width: "100%" } },
              }}
              ListboxProps={{ style: { maxHeight: 360 } }}
              renderOption={(props, option, { selected }) => {
                const isSelectable =
                  allowCampaignOnly || option.hasAdGroup;
                const content = (
                  <>
                    <Checkbox
                      size="small"
                      checked={selected}
                      sx={{ mr: 1 }}
                      disabled={!isSelectable}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor:
                              option.status?.toLowerCase() === "enabled"
                                ? "green"
                                : "lightgray",
                          }}
                        />
                        <span>{option.label}</span>
                      </Box>
                      {!option.hasAdGroup && (
                        <Box
                          style={{ fontSize: 12, marginLeft: 4, color: "#999" }}
                        >
                          (No ad-group)
                        </Box>
                      )}
                    </Box>
                  </>
                );

                return (
                  <li
                    {...props}
                    key={option.value}
                    style={{
                      opacity: isSelectable ? 1 : 0.5,
                      cursor: isSelectable ? "pointer" : "not-allowed",
                    }}
                  >
                    {content}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Campaign"
                  placeholder="Search for campaign"
                  size="medium"
                  error={!!errors.campaigns}
                  helperText={errors.campaigns}
                />
              )}
            />

            {/* AdGroup Select */}
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              options={mergedDataOptionsForDropDown ?? []}
              getOptionLabel={(opt) => opt.label}
              value={mergedDataOptionsForDropDown.filter((a) =>
                selectedAdGroups.includes(a.value)
              )}
              onChange={(e, newValue) => {
                setErrors((prev) => ({ ...prev, adGroups: "" }));
                const ids = newValue.map((v) => v.value);
                setSelectedAdGroups(ids);
              }}
              PopperProps={{
                sx: { "& .MuiPaper-root": { width: "100%" } },
              }}
              ListboxProps={{ style: { maxHeight: 360 } }}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                  <ListItemText primary={option.label} />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Ad Group"
                  placeholder="Search for ad-group"
                  size="medium"
                  error={!!errors.adGroups}
                  helperText={errors.adGroups}
                />
              )}
            />
          </Box>
        )}

        <Divider sx={{ background: "#E0E0E0" }} />

        <Box display="flex" gap={1} p={2.5}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="medium"
            disabled={isSaving}
            fullWidth
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="medium"
            sx={{ backgroundColor: "#6759FF", flex: 1, position: "relative" }}
            onClick={handleSaveProductSet}
            disabled={isSaving}
          >
            {isSaving ? (
              <CircularProgress
                size={24}
                color="inherit"
                sx={{ color: "white" }}
              />
            ) : (
              (selectedCampaigns?.length || selectedAdGroups?.length) ? "Populate" : "Save"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
